import React, { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Wallet, Users, HardHat, Contact, LineChart,
  Sparkles, Search, LogOut, Bell, HardHat as HatIcon, ChevronDown, Settings,
  ClipboardList, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import AIAssistant from "@/components/AIAssistant";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/finance", label: "Finance & EVM", icon: Wallet, tid: "nav-finance" },
  { to: "/payroll", label: "Payroll & Attendance", icon: Users, tid: "nav-payroll" },
  { to: "/labor", label: "Site Execution & Fab", icon: HardHat, tid: "nav-labor" },
  { to: "/timesheet", label: "Digital Timesheet", icon: ClipboardList, tid: "nav-timesheet" },
  { to: "/vendors", label: "Vendor Ledger", icon: Building2, tid: "nav-vendors" },
  { to: "/crm", label: "CRM", icon: Contact, tid: "nav-crm" },
  { to: "/mis", label: "MIS & Billing", icon: LineChart, tid: "nav-mis" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [aiOpen, setAiOpen] = useState(false);
  const location = useLocation();
  const current = NAV.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r bg-card hidden md:flex flex-col">
        <div className="h-14 flex items-center px-4 border-b">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center mr-2.5">
            <HatIcon className="h-4 w-4 text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">Artisan Guild</div>
            <div className="text-[11px] text-muted-foreground font-mono tracking-wider">INFRA · ERP</div>
          </div>
        </div>
        <nav className="p-3 space-y-0.5 flex-1 overflow-y-auto">
          <div className="uppercase text-[10px] tracking-wider text-muted-foreground font-mono px-3 py-2">
            Modules
          </div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
          <div className="uppercase text-[10px] tracking-wider text-muted-foreground font-mono px-3 py-2 mt-4">
            Tools
          </div>
          <button
            onClick={() => setAiOpen(true)}
            data-testid="nav-ai-assistant"
            className="sidebar-link w-full"
          >
            <Sparkles className="h-4 w-4" />
            <span>AI Assistant</span>
            <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5 bg-secondary/30 text-secondary-foreground border-secondary/40">Claude</Badge>
          </button>
        </nav>
        <div className="p-3 border-t">
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            System operational · v1.0
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 border-b bg-card px-4 md:px-6 flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-display font-semibold text-sm truncate">
              {current?.label || "Overview"}
            </span>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              / {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:flex items-center relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 text-muted-foreground" />
              <Input
                data-testid="global-search"
                placeholder="Search projects, employees, leads…"
                className="pl-8 h-9 w-64 text-sm bg-background border-border"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAiOpen(true)}
              data-testid="open-ai-assistant"
              className="h-9 gap-1.5 border-primary/20 hover:bg-accent"
            >
              <Sparkles className="h-3.5 w-3.5 text-secondary" />
              <span className="hidden sm:inline">Ask AI</span>
            </Button>
            <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Notifications">
              <Bell className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-accent transition-colors">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-[11px]">
                      {(user?.name || "U").split(" ").map(s => s[0]).slice(0,2).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left leading-tight hidden sm:block">
                    <div className="text-xs font-medium truncate max-w-[120px]">{user?.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono uppercase">{user?.role}</div>
                  </div>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Settings className="h-3.5 w-3.5 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                  <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1600px] mx-auto p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      <AIAssistant open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}
